import { useGoogleAuth, useGoogleLogout } from '@/shared/api'
import {
    GoogleDriveSVGIcon,
    GoogleSVGIcon,
    TrashSVGIcon,
} from '@/shared/assets'
import { useCopyToClipboard } from '@/shared/hooks'
import {
    CheckIcon,
    ContentContainer,
    Input,
    ShadcnButton,
    Toast,
} from '@/shared/ui'
import { GoogleUserInfoDto } from '@synapex/shared'
import {
    AccountWorkspaceDescriptor,
    getAldFromAwd,
} from '@synapex/shared2/entities'

type GoogleDriveLoginFormProps = {
    googleUserInfo: GoogleUserInfoDto | undefined | null
} & AccountWorkspaceDescriptor

export const GoogleDriveLoginForm: React.FC<GoogleDriveLoginFormProps> = ({
    googleUserInfo,
    ...props
}) => {
    const googleAuthMutation = useGoogleAuth(getAldFromAwd(props))
    const googleLogoutMutation = useGoogleLogout(getAldFromAwd(props))

    const { copy, toastVisible, toastMessage, hideToast } = useCopyToClipboard()

    const handleLogin = async () => {
        const { url: authUrl } = await googleAuthMutation.mutateAsync()
        window.location.href = authUrl
    }

    const handleLogout = async () => {
        if (!googleUserInfo) return
        await googleLogoutMutation.mutateAsync()
    }

    const handleCopy = (e: React.MouseEvent) => {
        if (!googleUserInfo) return
        e.stopPropagation()
        copy(googleUserInfo.folderUrl)
    }

    return (
        <ContentContainer>
            <div className="flex items-center gap-[10px] pb-3">
                <h1 className="flex items-center gap-1 text-lg font-extrabold">
                    Папка на <GoogleDriveSVGIcon /> Google Drive для документов
                </h1>
                <CheckIcon className="h-4 w-4" checked={!!googleUserInfo} />
            </div>
            <span className="inline-block w-[320px] pb-[14px] text-xs">
                Авторизуйтесь и сервис самостоятельно создаст папку для хранения
                документов на вашем диске
            </span>
            {googleUserInfo ? (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-[10px]">
                        <ShadcnButton
                            variant="outline"
                            className="flex items-center gap-[10px] border-[#747775] p-[10px] font-['Roboto'] text-sm text-stone-900 hover:bg-slate-50"
                        >
                            <img
                                src={googleUserInfo.picture}
                                alt="Google user icon"
                                className="h-5 w-5 rounded-full"
                            />
                            <div className="flex flex-col items-start">
                                <span className="text-[10px] leading-3">
                                    {googleUserInfo.name}
                                </span>
                                <span className="text-[8px] leading-[8px] text-black/30">
                                    {googleUserInfo.email}
                                </span>
                            </div>
                        </ShadcnButton>

                        <ShadcnButton onClick={handleLogout} className="p-0">
                            <TrashSVGIcon />
                        </ShadcnButton>
                    </div>

                    <Input
                        onClick={handleCopy}
                        innerClassName="h-[32px] text-blue-primary cursor-pointer w-72"
                        value={googleUserInfo.folderUrl}
                        readOnly
                        shouldShowClearButton={false}
                    />

                    <Toast
                        message={toastMessage}
                        visible={toastVisible}
                        onClose={hideToast}
                        greenSwooshIcon={true}
                    />
                </div>
            ) : (
                <ShadcnButton
                    variant="outline"
                    onClick={handleLogin}
                    className="flex items-center gap-2 border-[#747775] font-['Roboto'] text-sm text-stone-900 hover:bg-slate-50"
                >
                    <GoogleSVGIcon />
                    Войти через Google
                </ShadcnButton>
            )}
        </ContentContainer>
    )
}
